import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus,
  Search,
  Filter,
  Edit,
  Trash2,
  Package,
  Eye,
  X,
  Save,
} from 'lucide-react';
import toast from 'react-hot-toast';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import { api } from '@/services/api';

interface Product {
  id: string;
  sku: string;
  name: string;
  description?: string;
  price: number;
  cost?: number;
  category: string;
  taxRate: number;
  stockQuantity: number;
  isActive: boolean;
  createdAt: string | Date;
  updatedAt: string | Date;
}

const ProductsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const lowStockThreshold = 5;

  // Form state
  const [formData, setFormData] = useState({
    sku: '',
    name: '',
    description: '',
    price: '',
    cost: '',
    category: '',
    taxRate: '16.5',
    stockQuantity: '',
    isActive: true,
  });

  const {
    data: products = [],
    isLoading,
    isError,
  } = useQuery<Product[]>({
    queryKey: ['products'],
    queryFn: async () => {
      console.log('🔍 Fetching products from API...');
      try {
        const res = await api.get('/api/products');
        console.log('📦 Products API response:', res.data);
        const productsData = (res.data?.data?.products || res.data?.products || []) as Product[];
        console.log('📋 Processed products data:', productsData);
        return productsData;
      } catch (error) {
        console.error('❌ Products fetch error:', error);
        throw error;
      }
    },
  });

  // Mutations
  const createMutation = useMutation({
    mutationFn: async (payload: typeof formData) => {
      console.log('🚀 Creating product with payload:', payload);
      try {
        const productPayload = {
          ...payload,
          price: parseFloat(payload.price),
          cost: payload.cost ? parseFloat(payload.cost) : undefined,
          taxRate: parseFloat(payload.taxRate),
          stockQuantity: parseInt(payload.stockQuantity),
        };
        const res = await api.post('/api/products', productPayload);
        console.log('✅ Product creation response:', res.data);
        return res.data?.product;
      } catch (error) {
        console.error('❌ Product creation error:', error);
        throw error;
      }
    },
    onMutate: async (newProduct) => {
      console.log('🔄 Starting optimistic update for:', newProduct);
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['products'] });
      
      // Snapshot the previous value
      const previousProducts = queryClient.getQueryData(['products']);
      console.log('📸 Previous products snapshot:', previousProducts);
      
      // Optimistically update to the new value
      const optimisticProduct = {
        id: Date.now().toString(),
        sku: `SKU-${Date.now()}`,
        name: newProduct.name,
        price: parseFloat(newProduct.price),
        category: newProduct.category,
        stockQuantity: parseInt(newProduct.stockQuantity),
        isActive: true,
        createdAt: new Date().toISOString(),
      };
      
      console.log('⚡ Adding optimistic product:', optimisticProduct);
      
      queryClient.setQueryData(['products'], (old: any) => {
        const currentProducts = Array.isArray(old) ? old : [];
        const newProducts = [...currentProducts, optimisticProduct];
        console.log('📝 Updated products array:', newProducts);
        return newProducts;
      });
      
      return { previousProducts };
    },
    onError: (_err, _newProduct, context) => {
      console.error('💥 Product creation failed, rolling back');
      // If the mutation fails, use the context returned from onMutate
      if (context?.previousProducts) {
        console.log('🔄 Restoring previous products');
        queryClient.setQueryData(['products'], context.previousProducts);
      }
      toast.error('Failed to create product');
    },
    onSuccess: (data) => {
      console.log('🎉 Product creation successful:', data);
      setIsCreateModalOpen(false);
      resetForm();
      toast.success('Product created successfully');
    },
    onSettled: () => {
      console.log('🏁 Product creation settled, invalidating cache');
      // Always refetch after error or success
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: typeof formData }) => {
      const res = await api.put(`/products/${id}`, {
        ...payload,
        price: payload.price ? parseFloat(payload.price) : undefined,
        cost: payload.cost ? parseFloat(payload.cost) : undefined,
        taxRate: payload.taxRate ? parseFloat(payload.taxRate) : undefined,
        stockQuantity: payload.stockQuantity ? parseInt(payload.stockQuantity) : undefined,
      });
      return res.data?.product;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      setIsEditModalOpen(false);
      setEditingProduct(null);
      resetForm();
      toast.success('Product updated successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to update product');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/products/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      toast.success('Product deleted successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to delete product');
    },
  });

  const resetForm = () => {
    setFormData({
      sku: '',
      name: '',
      description: '',
      price: '',
      cost: '',
      category: '',
      taxRate: '16.5',
      stockQuantity: '',
      isActive: true,
    });
  };

  const handleCreate = () => {
    createMutation.mutate(formData);
  };

  const handleEdit = (product: Product) => {
    setEditingProduct(product);
    setFormData({
      sku: product.sku,
      name: product.name,
      description: product.description || '',
      price: product.price.toString(),
      cost: product.cost?.toString() || '',
      category: product.category,
      taxRate: product.taxRate.toString(),
      stockQuantity: product.stockQuantity.toString(),
      isActive: product.isActive,
    });
    setIsEditModalOpen(true);
  };

  const handleUpdate = () => {
    if (!editingProduct) return;
    updateMutation.mutate({ id: editingProduct.id, payload: formData });
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      deleteMutation.mutate(id);
    }
  };

  const filteredProducts = products.filter((product: Product) => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.sku.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = showInactive || product.isActive;
    return matchesSearch && matchesStatus;
  });

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-MW', {
      style: 'currency',
      currency: 'MWK',
    }).format(amount);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Products
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            Manage your product inventory
          </p>
        </div>
        <Button onClick={() => setIsCreateModalOpen(true)} className="flex items-center">
          <Plus className="h-4 w-4 mr-2" />
          Add Product
        </Button>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-body">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <Input
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                icon={<Search className="h-4 w-4 text-gray-400" />}
              />
            </div>
            <div className="flex items-center space-x-4">
              <Button variant="ghost" size="sm">
                <Filter className="h-4 w-4 mr-2" />
                Filters
              </Button>
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={showInactive}
                  onChange={(e) => setShowInactive(e.target.checked)}
                  className="mr-2"
                />
                <span className="text-sm text-gray-700">
                  Show inactive
                </span>
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Products Grid */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {isLoading && (
          <div className="col-span-1 sm:col-span-2 lg:col-span-3 xl:col-span-4 text-center text-sm text-gray-600 py-12">
            Loading products...
          </div>
        )}
        {isError && (
          <div className="col-span-1 sm:col-span-2 lg:col-span-3 xl:col-span-4 text-center text-sm text-red-600 py-12">
            Failed to load products
          </div>
        )}
        {filteredProducts.map((product: Product, index: number) => (
          <motion.div
            key={product.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: index * 0.05 }}
            className="card hover:shadow-medium transition-shadow duration-200"
          >
            <div className="p-6">
              {/* Product Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">
                    {product.name}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {product.sku}
                  </p>
                </div>
                <div className={`px-2 py-1 text-xs font-medium rounded-full ${
                  product.isActive 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-gray-100 text-gray-800'
                }`}>
                  {product.isActive ? 'Active' : 'Inactive'}
                </div>
              </div>

              {/* Product Details */}
              <div className="space-y-3">
                {product.description && (
                  <p className="text-sm text-gray-600 line-clamp-2">
                    {product.description}
                  </p>
                )}

                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-500">Unit Price</p>
                    <p className="text-lg font-bold text-gray-900">
                      {formatCurrency(product.price)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-500">Stock</p>
                    <p className={`text-lg font-bold ${
                      product.stockQuantity <= lowStockThreshold
                        ? 'text-red-600'
                        : 'text-gray-900'
                    }`}>
                      {product.stockQuantity}
                    </p>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-between pt-4 border-t border-gray-100 mt-4">
                <div className="flex items-center space-x-2">
                  <Button variant="ghost" size="sm">
                    <Eye className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleEdit(product)}>
                    <Edit className="h-4 w-4" />
                  </Button>
                </div>
                <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700" onClick={() => handleDelete(product.id)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Empty State */}
      {filteredProducts.length === 0 && (
        <div className="text-center py-12">
          <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No products found
          </h3>
          <p className="text-gray-600">
            {searchTerm 
              ? 'No products match your search criteria.'
              : 'No products available. Add your first product to get started.'
            }
          </p>
          {!searchTerm && (
            <Button onClick={() => setIsCreateModalOpen(true)} className="mt-4">
              <Plus className="h-4 w-4 mr-2" />
              Add Your First Product
            </Button>
          )}
        </div>
      )}

      {/* Create/Edit Modal */}
      {(isCreateModalOpen || isEditModalOpen) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">
                {isCreateModalOpen ? 'Create Product' : 'Edit Product'}
              </h2>
              <Button variant="ghost" size="sm" onClick={() => {
                setIsCreateModalOpen(false);
                setIsEditModalOpen(false);
                setEditingProduct(null);
                resetForm();
              }}>
                <X className="h-4 w-4" />
              </Button>
            </div>

            <div className="space-y-4">
              <Input
                label="SKU"
                value={formData.sku}
                onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                placeholder="SKU-001"
                required
              />
              <Input
                label="Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Product name"
                required
              />
              <Input
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Optional description"
              />
              <Input
                label="Price"
                type="number"
                step="0.01"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                placeholder="0.00"
                required
              />
              <Input
                label="Cost"
                type="number"
                step="0.01"
                value={formData.cost}
                onChange={(e) => setFormData({ ...formData, cost: e.target.value })}
                placeholder="0.00"
              />
              <Input
                label="Category"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                placeholder="Category"
                required
              />
              <Input
                label="Tax Rate (%)"
                type="number"
                step="0.1"
                value={formData.taxRate}
                onChange={(e) => setFormData({ ...formData, taxRate: e.target.value })}
                placeholder="16.5"
              />
              <Input
                label="Stock Quantity"
                type="number"
                value={formData.stockQuantity}
                onChange={(e) => setFormData({ ...formData, stockQuantity: e.target.value })}
                placeholder="0"
              />
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  className="mr-2"
                />
                <span className="text-sm text-gray-700">Active</span>
              </label>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <Button variant="ghost" onClick={() => {
                setIsCreateModalOpen(false);
                setIsEditModalOpen(false);
                setEditingProduct(null);
                resetForm();
              }}>
                Cancel
              </Button>
              <Button
                onClick={isCreateModalOpen ? handleCreate : handleUpdate}
                disabled={createMutation.isPending || updateMutation.isPending}
              >
                <Save className="h-4 w-4 mr-2" />
                {isCreateModalOpen ? 'Create' : 'Update'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductsPage;
